import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, CreditCard, Wallet, ReceiptIndianRupee, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { inr } from "@/components/bits";
import api, { errMsg } from "@/lib/api";

// Shared posting-payment picker: free first job / wallet / per-job fee / postpaid / plan.
export function PostPaymentDialog({ open, onClose, onChoose, busy }) {
  const [opts, setOpts] = useState(null);
  const [mode, setMode] = useState("");

  useEffect(() => {
    if (open) {
      setMode("");
      api.get("/employer/posting-options")
        .then(({ data }) => setOpts(data))
        .catch((e) => toast.error(errMsg(e)));
    }
  }, [open]);

  const options = opts ? [
    {
      id: "free", icon: BadgeCheck, title: "First job free",
      desc: "Your first job post is on us — no charge.", price: "₹0",
      disabled: !opts.free_eligible,
      disabledHint: "Already used",
    },
    {
      id: "wallet", icon: Wallet, title: "Wallet credit (prepaid)",
      desc: `Use 1 recruitment credit · ${opts.wallet_balance} available. Recharge from Wallet & Billing.`,
      price: "1 credit",
      disabled: opts.wallet_balance < 1,
      disabledHint: "No credits",
    },
    {
      id: "per_job", icon: CreditCard, title: "Pay per job",
      desc: "One-time posting fee via PayU — cards, UPI, netbanking.",
      price: `${inr(opts.per_job_fee)} incl. GST`,
      disabled: false,
    },
    {
      id: "postpaid", icon: ReceiptIndianRupee, title: "Postpaid",
      desc: `${opts.postpaid_pct}% of annual CTC + GST, invoiced only on successful joining.`,
      price: "Pay on joining",
      disabled: false,
    },
    {
      id: "plan", icon: CalendarClock, title: opts.plan ? `Your ${opts.plan.plan_name} plan` : "Hiring subscription",
      desc: opts.plan
        ? `${Math.max(0, opts.plan_slots_left)} job slot(s) left in your current plan.`
        : "No active hiring plan — subscribe from the Subscription page for bundled postings.",
      price: opts.plan ? "Included" : "From ₹2,999/mo",
      disabled: !opts.plan || opts.plan_slots_left <= 0,
      disabledHint: opts.plan ? "Plan slots full" : "No plan",
    },
  ] : [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-lg" data-testid="posting-payment-dialog">
        <DialogHeader><DialogTitle>Choose how to pay for this posting</DialogTitle></DialogHeader>
        {!opts ? (
          <p className="text-sm text-muted-foreground py-6">Loading payment options…</p>
        ) : (
          <div className="space-y-2.5" role="radiogroup" aria-label="Posting payment">
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                disabled={o.disabled}
                onClick={() => setMode(o.id)}
                data-testid={`pay-mode-${o.id}`}
                className={`w-full text-left border rounded-xl p-4 flex items-center gap-4 transition-colors ${
                  o.disabled ? "opacity-45 cursor-not-allowed" :
                  mode === o.id ? "border-[#2563EB] bg-[#2563EB]/5 ring-2 ring-[#2563EB]/25" : "border-border hover:border-[#2563EB]/40"
                }`}
              >
                <o.icon className="w-5 h-5 text-[#2563EB] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm text-[#0B1B3D]">
                    {o.title} {o.disabled && o.disabledHint && <span className="text-[10px] text-muted-foreground">({o.disabledHint})</span>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{o.desc}</p>
                </div>
                <p className="font-heading font-bold text-sm text-[#0B1B3D] shrink-0">{o.price}</p>
              </button>
            ))}
          </div>
        )}
        <Button
          onClick={() => onChoose(mode)}
          disabled={!mode || busy}
          className="w-full rounded-full bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-bold mt-2"
          data-testid="pay-mode-confirm-btn"
        >
          {busy ? "Processing…" : mode === "per_job" ? "Pay & Submit for Review" : "Submit for Review"}
        </Button>
        <p className="text-[11px] text-muted-foreground">If you leave before paying, the job stays saved as a draft — pay anytime from Jobs.</p>
      </DialogContent>
    </Dialog>
  );
}
